import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, DatePickerAPI, FetchAPI, FilterAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeSubmit, OnClear, OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { NonLabExamsListAliases } from '../../../codegen';

import { Endpoint } from '../../../constants/endpoints';
import { ApplicationRoute } from '../../../constants/routes';
import { DateFilterUtils } from '../../../utils/date-filter-utils';
import { FoundationUtils } from '../../../utils/foundation-utils';
import BaseController from '../../BaseController';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import ShowToast from '../../../utils/toast-service';

interface FilterParam {
  cdTipoExame: number;
  dsStatus?: number;
  dtFinal: Date;
  dtInicial: Date;
  examPresentationId?: number;
}

@Controller({ component: 'layout-non-lab-exams', fragment: 'non-lab-exams-list' })
class NonLabExamsListController extends BaseController {

  @Inject(NonLabExamsListAliases.AUTOCOMPLETE_EXAM_TYPES)
    autoCompleteExamType!: AutocompleteAPI;

  @Inject('date-picker-start-date')
    dpaStartDate!: DatePickerAPI;

  @Inject('date-picker-end-date')
    dpaEndDate!: DatePickerAPI;

  @Inject(NonLabExamsListAliases.FETCH_NON_LAB_EXAMS)
    fetchNonLabExamsApi!: FetchAPI;

  @Inject(NonLabExamsListAliases.FILTER_NON_LAB_EXAMS)
    filter!: FilterAPI;

  internalNavigation!: boolean;

  @OnClear({ target: 'filter-non-lab-exams' })
  onFilterClear(): void {
    this.filter.clear();
    this.autoCompleteExamType.setValue({} as Record<string, Value>)
  }

  @OnMount({ target: 'layout-non-lab-exams' })
  onMount(): void {
    InformationBarUtils.collapsedBar = true;
    this.internalNavigation = false;
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const PEP_MODULE = 'PEP#281';

    const paramViewAnatomyReport = FoundationUtils.findModuleParam(PEP_MODULE, 'VIEW_ANATOMY_REPORT#208');
    const paramRequiresReportRelease = FoundationUtils.findModuleParam(PEP_MODULE, 'REQUIRES_REPORT_RELEASE#16');
    const paramViewTyping = FoundationUtils.findModuleParam(PEP_MODULE, 'VIEW_TYPING#1307');
    const paramShowOnlyApprovedNonLabExams = FoundationUtils.findModuleParam(PEP_MODULE, 'SHOW_ONLY_APPROVED_NON_LAB_EXAMS#724');

    this.context.set({
      encounterId: encounterId as number,
      paramRequiresReportRelease: paramRequiresReportRelease as string,
      paramShowOnlyApproved: paramShowOnlyApprovedNonLabExams as string,
      paramViewAnatomyReport: paramViewAnatomyReport as string,
      paramViewTyping: paramViewTyping as string,
      patientId: patientId as string,
    });
  }

  @OnUnmount({ target: 'layout-non-lab-exams' })
  onUnmount(): void {
    InformationBarUtils.collapsedBar = false;
  }

  @OnMount({ target: 'filter-non-lab-exams' })
  onFilterMount(event: MountEvent): void {

    let record = {
      dtFinal: '',
      dtInicial:'',
      examPresentationId: {}
    };

    const filter = event.target as FilterAPI;
    const restored = filter.restoreState(true);

    if ( !restored ) {

      const {
        lastMonth,
        currentDate
      } = DateFilterUtils.getDateRange();

      const presentationExams = {
        description: MessageService.translate('text.exam-presentation'),
        id: 1
      };

      record = {
        dtFinal: currentDate,
        dtInicial: lastMonth,
        examPresentationId: presentationExams
      };

    } else {

      Object.assign( record, filter.getRecord() );

    }

    filter.setData(record);
    filter.submit();

  }

  @OnUnmount({ target: 'filter-non-lab-exams' })
  OnFilterUnmount(e: MountEvent): void {

    const filter = e.target as FilterAPI;

    if ( this.internalNavigation ) {

      const record = filter.getRecord() as Record<string, FilterParam>;
      filter.setData(record);

      filter.persistState();

    } else {

      filter.clearPersistedState();

    }

  }

  @OnClick({ target: 'deck-non-lab-exams' })
  onCardClick(event: ClickEvent): void {

    this.internalNavigation = true;

    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const { nrSequencia, nrLaudo } = event.value;

    HttpService.post(
      Endpoint.REGISTER_NON_LAB_EXAM_ACCESS_LOG, {
        encounterId,
        nrSequencia: nrLaudo || nrSequencia,
        patientId,
      }
    ).then(() => {
      applicationContext.redirect(ApplicationRoute.ELECTRONIC_PATIENT_CHART +
        ApplicationRoute.PATIENT_LIST +
        ApplicationRoute.NON_LAB_EXAMS_LIST +
        ApplicationRoute.NON_LAB_EXAM_DETAIL, { nrSequencia });
    });
  }

  @OnMount({ target: NonLabExamsListAliases.AUTOC_NON_EXAM_TYPE })
  handleMountPresentation(event: MountEvent): void {

    const fieldApi = event.target as AutocompleteAPI;

    const data = [
      {
        description: MessageService.translate('text.exam-presentation'),
        id: 1,
      },
      {
        description: MessageService.translate('text.all-patient'),
        id: 2,
      }
    ]

    fieldApi.setData(data);

    fieldApi.setValue(data[0]);
  }

  @OnMount({ target: NonLabExamsListAliases.AUTOCOMPLETE_STATUSES})
  handleMountStatuses(event: MountEvent): void {

    const fieldApi = event.target as AutocompleteAPI;

    const data = [
      {
        dsStatus: MessageService.translate('tws.status.available'),
        id: 1,
      },
      {
        dsStatus: MessageService.translate('tws.status.processing'),
        id: 2,
      }
    ]

    fieldApi.setData(data);

  }

  @OnBeforeSubmit({ target: NonLabExamsListAliases.FILTER_NON_LAB_EXAMS })
  handleFilterNonLabExamsBeforeSubmit(event: SubmitEvent) : void {

    const params = {
      dsStatus: event.record.dsStatus,
      dtFinal: event.record.dtFinal,
      dtInicial: event.record.dtInicial,
      examPresentationId: event.record.examPresentationId
    } as FilterParam;

    if(event.record.cdTipoExame) {
      params.cdTipoExame = event.record.examTypeId;
    }

    if(DateFilterUtils.diffDate(params.dtFinal, params.dtInicial) > 90) {
      ShowToast.open(MessageService.translate('text.message.more-ninety-days'), ToastLevel.CAUTION);
    }

    this.fetchNonLabExamsApi.setParameters(params as unknown as Record<string, FilterParam>);
  }
}

export default NonLabExamsListController;
